import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Download, TrendingUp, TrendingDown } from 'lucide-react';
import { cropYieldData, weatherData, provinces, crops } from '../data/mockData';

const DataTable = () => {
  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [viewType, setViewType] = useState('crop'); // 'crop' or 'weather'
  
  const years = Array.from({length: 10}, (_, i) => (2024 - i).toString());
  
  const getCropData = () => {
    return provinces.map(province => {
      const data = cropYieldData[selectedCrop][province.id].find(d => d.year === parseInt(selectedYear));
      const prevYearData = cropYieldData[selectedCrop][province.id].find(d => d.year === parseInt(selectedYear) - 1);
      
      const yieldChange = prevYearData ? ((data.yield - prevYearData.yield) / prevYearData.yield * 100) : 0;
      const productionChange = prevYearData ? ((data.production - prevYearData.production) / prevYearData.production * 100) : 0;
      
      return {
        province: province.name,
        yield: data.yield,
        yieldChange,
        area: data.area,
        production: data.production,
        productionChange
      };
    });
  };
  
  const getWeatherData = () => {
    return provinces.map(province => {
      const tempData = weatherData[province.id].temperature.find(d => d.year === parseInt(selectedYear));
      const precipData = weatherData[province.id].precipitation.find(d => d.year === parseInt(selectedYear));
      
      return {
        province: province.name,
        avgTemp: tempData.avg,
        minTemp: tempData.min,
        maxTemp: tempData.max,
        totalPrecip: precipData.total,
        growingPrecip: precipData.growing
      };
    });
  };
  
  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return null;
  };
  
  const formatChange = (change) => {
    if (change === 0) return '-';
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };
  
  const exportData = () => {
    const data = viewType === 'crop' ? getCropData() : getWeatherData();
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${viewType}_data_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Agricultural Data Table</span>
          <div className="flex gap-2">
            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="crop">Crop Data</SelectItem>
                <SelectItem value="weather">Weather Data</SelectItem>
              </SelectContent>
            </Select>
            
            {viewType === 'crop' && (
              <Select value={selectedCrop} onValueChange={setSelectedCrop}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {crops.map(crop => (
                    <SelectItem key={crop} value={crop}>
                      {crop}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={exportData} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {viewType === 'crop' ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Province</TableHead>
                <TableHead>Yield (t/ha)</TableHead>
                <TableHead>Yield Change</TableHead>
                <TableHead>Area (ha)</TableHead>
                <TableHead>Production (t)</TableHead>
                <TableHead>Production Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getCropData().map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{row.province}</TableCell>
                  <TableCell>{row.yield.toFixed(1)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(row.yieldChange)}
                      <span className={row.yieldChange > 0 ? 'text-green-600' : row.yieldChange < 0 ? 'text-red-600' : 'text-gray-600'}>
                        {formatChange(row.yieldChange)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{row.area.toLocaleString()}</TableCell>
                  <TableCell>{row.production.toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(row.productionChange)}
                      <span className={row.productionChange > 0 ? 'text-green-600' : row.productionChange < 0 ? 'text-red-600' : 'text-gray-600'}>
                        {formatChange(row.productionChange)}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Province</TableHead>
                <TableHead>Avg Temp (°C)</TableHead>
                <TableHead>Min Temp (°C)</TableHead>
                <TableHead>Max Temp (°C)</TableHead>
                <TableHead>Total Precip (mm)</TableHead>
                <TableHead>Growing Precip (mm)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getWeatherData().map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{row.province}</TableCell>
                  <TableCell>{row.avgTemp.toFixed(1)}</TableCell>
                  <TableCell className="text-blue-600">{row.minTemp.toFixed(1)}</TableCell>
                  <TableCell className="text-red-600">{row.maxTemp.toFixed(1)}</TableCell>
                  <TableCell>{row.totalPrecip}</TableCell>
                  <TableCell className="text-green-600">{row.growingPrecip}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default DataTable;